import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Divider,
  Badge,
  Fade,
  Popper,
  ClickAwayListener
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  FilterAlt as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { seoConfig } from '../config/seoConfig';

const SmartSearch = ({ 
  variant = 'header', // 'header', 'hero', 'modal'
  placeholder = "Rechercher votre métier, ville...",
  showTrending = true,
  onClose = null 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    metiers: [],
    cities: [],
    services: []
  });
  
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const anchorRef = useRef(null);

  // Données statiques pour les suggestions
  const trendingData = [
    { query: 'électricien paris', count: 1250, icon: '⚡' },
    { query: 'plombier lyon', count: 890, icon: '🔧' },
    { query: 'coiffeur marseille', count: 756, icon: '💇' },
    { query: 'garagiste toulouse', count: 634, icon: '🚗' },
    { query: 'menuisier bordeaux', count: 512, icon: '🪚' }
  ];

  const allCities = [
    'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Bordeaux', 'Lille', 'Nantes',
    'Strasbourg', 'Montpellier', 'Rennes', 'Reims', 'Saint-Étienne', 'Toulon',
    'Le Havre', 'Grenoble', 'Dijon', 'Angers', 'Nîmes', 'Villeurbanne'
  ];

  useEffect(() => {
    // Charger les recherches récentes depuis le localStorage
    const saved = localStorage.getItem('lys-recent-searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
    
    // Initialiser les trending searches
    setTrendingSearches(trendingData);
  }, []);

  // Fonction de recherche intelligente
  const performSearch = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();

    // Recherche dans les métiers
    Object.entries(seoConfig.metiers).forEach(([metier, config]) => {
      const metierName = config.title.split('|')[0].trim();
      
      if (metierName.toLowerCase().includes(lowerQuery) || 
          metier.toLowerCase().includes(lowerQuery)) {
        results.push({
          type: 'metier',
          id: metier,
          title: metierName,
          subtitle: `Solutions pour ${metier}`,
          path: `/${metier}`,
          icon: getMetierIcon(metier),
          score: metierName.toLowerCase().indexOf(lowerQuery) === 0 ? 100 : 80
        });

        // Recherche dans les services du métier
        if (config.services) {
          config.services.forEach(service => {
            if (service.name.toLowerCase().includes(lowerQuery)) {
              results.push({
                type: 'service',
                id: `${metier}-${service.name}`,
                title: service.name,
                subtitle: `${metierName} - ${service.description || service.name}`,
                path: `/${metier}/${service.name.toLowerCase().replace(/\s+/g, '-')}`,
                icon: '🛠️',
                score: 60
              });
            }
          });
        }

        // Recherche géographique
        if (config.cities) {
          config.cities.forEach(city => {
            if (city.name.toLowerCase().includes(lowerQuery)) {
              results.push({
                type: 'location',
                id: `${metier}-${city.name}`,
                title: `${metierName} ${city.name}`,
                subtitle: `Spécialiste à ${city.name}`,
                path: `/${metier}-${city.name.toLowerCase().replace(/\s+/g, '-')}`,
                icon: '📍',
                score: 70
              });
            }
          });
        }
      }
    });

    // Recherche dans toutes les villes
    allCities.forEach(city => {
      if (city.toLowerCase().includes(lowerQuery)) {
        Object.keys(seoConfig.metiers).slice(0, 3).forEach(metier => {
          const config = seoConfig.metiers[metier];
          results.push({
            type: 'location',
            id: `${metier}-${city}`,
            title: `${config.title.split('|')[0].trim()} ${city}`,
            subtitle: `Solutions à ${city}`,
            path: `/${metier}-${city.toLowerCase()}`,
            icon: '🌍',
            score: 50
          });
        });
      }
    });

    // Trier par score et pertinence
    const sortedResults = results
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);

    setSearchResults(sortedResults);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    performSearch(query);
    setIsOpen(query.length > 0);
  };

  const handleResultClick = (result) => {
    // Sauvegarder dans les recherches récentes
    const newSearch = {
      query: result.title,
      path: result.path,
      timestamp: Date.now()
    };
    
    const updated = [newSearch, ...recentSearches.filter(r => r.path !== result.path)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('lys-recent-searches', JSON.stringify(updated));
    
    // Naviguer
    navigate(result.path);
    setIsOpen(false);
    setSearchQuery('');
    
    if (onClose) onClose();
  };

  const handleTrendingClick = (trending) => {
    setSearchQuery(trending.query);
    performSearch(trending.query);
    setIsOpen(true);
  };

  const getMetierIcon = (metier) => {
    const icons = {
      electriciens: '⚡',
      plombiers: '🔧',
      coiffeurs: '💇',
      garagistes: '🚗',
      jardiniers: '🌱',
      peintres: '🎨',
      macons: '🏗️',
      menuisiers: '🪚',
      fleuristes: '🌸',
      carreleurs: '🏠',
      estheticiennes: '💅',
      masseurs: '🤲',
      menage: '🧹',
      carrossiers: '🔧',
      'controle-technique': '🛡️',
      ramoneurs: '🔥',
      couvreurs: '🏘️'
    };
    return icons[metier] || '🔧';
  };

  // Composant des résultats de recherche
  const SearchResults = () => (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 1300,
        maxHeight: 400,
        overflow: 'auto',
        borderRadius: 2,
        mt: 1,
        border: '1px solid #e2e8f0'
      }}
    >
      {searchResults.length > 0 ? (
        <List sx={{ p: 0 }}>
          {searchResults.map((result, index) => (
            <ListItem
              key={result.id}
              button
              onClick={() => handleResultClick(result)}
              sx={{
                borderBottom: index === searchResults.length - 1 ? 'none' : '1px solid #f1f5f9',
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.04)'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'transparent', color: '#64748b' }}>
                  {result.icon}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="medium">
                    {result.title}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {result.subtitle}
                    </Typography>
                    <Chip
                      label={result.type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 16, fontSize: '0.65rem' }}
                    />
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : searchQuery.length > 2 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Aucun résultat pour "{searchQuery}"
          </Typography>
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => navigate('/contact')}
          >
            Contactez-nous pour votre métier
          </Button>
        </Box>
      ) : null}
    </Paper>
  );

  // Suggestions par défaut (trending + récentes)
  const DefaultSuggestions = () => (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 1300,
        maxHeight: 500,
        overflow: 'auto',
        borderRadius: 2,
        mt: 1,
        border: '1px solid #e2e8f0'
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Recherches récentes */}
        {recentSearches.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: '#64748b', textTransform: 'uppercase', mb: 1, display: 'block' }}
            >
              <HistoryIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Recherches récentes
            </Typography>
            {recentSearches.map((recent, index) => (
              <Chip
                key={index}
                label={recent.query}
                size="small"
                clickable
                onClick={() => navigate(recent.path)}
                onDelete={() => {
                  const updated = recentSearches.filter((_, i) => i !== index);
                  setRecentSearches(updated);
                  localStorage.setItem('lys-recent-searches', JSON.stringify(updated));
                }}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}

        {/* Trending searches */}
        {showTrending && (
          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: '#64748b', textTransform: 'uppercase', mb: 1, display: 'block' }}
            >
              <TrendingIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Recherches populaires
            </Typography>
            <Grid container spacing={1}>
              {trendingSearches.map((trending, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<span>{trending.icon}</span>}
                    onClick={() => handleTrendingClick(trending)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderColor: '#e2e8f0',
                      color: '#64748b',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        bgcolor: 'rgba(59, 130, 246, 0.04)'
                      }
                    }}
                  >
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      <Typography variant="caption" display="block">
                        {trending.query}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {trending.count} recherches
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Paper>
  );

  // Rendu selon le variant
  const renderSearchInput = () => (
    <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        ref={searchInputRef}
        fullWidth
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={() => setIsOpen(true)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8' }} />
            </InputAdornment>
          ),
          endAdornment: isOpen && (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={() => {
                  setIsOpen(false);
                  setSearchQuery('');
                  if (onClose) onClose();
                }}
              >
                <CloseIcon fontSize="small" />
              </Button>
            </InputAdornment>
          ),
          sx: {
            borderRadius: variant === 'hero' ? 3 : 2,
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e2e8f0'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
              borderWidth: 2
            }
          }
        }}
        size={variant === 'hero' ? 'large' : 'medium'}
      />

      {/* Résultats ou suggestions */}
      <AnimatePresence>
        {isOpen && (
          <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {searchQuery.length > 0 ? <SearchResults /> : <DefaultSuggestions />}
            </motion.div>
          </ClickAwayListener>
        )}
      </AnimatePresence>
    </Box>
  );

  if (variant === 'modal') {
    return (
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Rechercher votre solution
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {renderSearchInput()}
        </DialogContent>
      </Dialog>
    );
  }

  return renderSearchInput();
};

// Hook pour la recherche globale
export const useSmartSearch = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);

  return {
    isSearchOpen,
    openSearch,
    closeSearch,
    SearchModal: () => isSearchOpen ? (
      <SmartSearch 
        variant="modal" 
        onClose={closeSearch}
        showTrending={true}
      />
    ) : null
  };
};

export default SmartSearch;